import { Component, OnDestroy, OnInit } from '@angular/core';
import {QuestionBase} from "../models/question-base";
import {Observable, of, Subject} from "rxjs";
import {filter, takeUntil} from "rxjs/operators";
import {QuestionService} from "../services/question.service";
import {ApiUrl} from "../../../shared/models/apiurl.models";
import {AdvFormService} from '../services/adv-form.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';

export type PublishFormMode = 'manual' | 'adaline';

// SCRUM-154: the question that picks the sub-category (private / commercial /
// jeeps / motorcycle / scooter ...). A sub-category may overload the form.
const SUB_CATEGORY_KEY = 'sconderyCategory';

@Component({
    selector: 'app-car-adv-form',
    templateUrl: './car-adv-form.component.html',
    styleUrls: ['./car-adv-form.component.css'],
    providers: [QuestionService],
    standalone: false
})
export class CarAdvFormComponent implements OnInit, OnDestroy {

  questions$!: Observable<QuestionBase<any>[]>;
  selectedMode: PublishFormMode | null = null;
  categoryLabel = '';
  // SCRUM-154: bumped whenever the question set is swapped for an overloaded
  // (or back to the base) form, so the manual / Adaline form is re-created
  // from the new questions.
  formVersion = 0;

  private readonly namespace: string;
  private currentQuestions: QuestionBase<any>[] = [];
  private loadedSubCategoryId: string | null = null;
  // The sub-category the current answers belong to. Picking a different one
  // wipes every answer and restarts the form from scratch.
  private answeredSubCategoryId: string | null = null;
  private reloadSequence = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(private service: QuestionService, private apiUrl: ApiUrl, private formService: AdvFormService,
              private http: HttpClient) {
    const namespace = this.apiUrl.getNameSpace()
    this.namespace = namespace;
    this.categoryLabel = this.formService.getQuestion('mainCategory')?.name || namespace;
    this.ensureMainCategory();
    service.getServerQuestions(namespace).subscribe((res)=>{
      res.subscribe((questions: QuestionBase<any>[]) => {
        this.currentQuestions = questions || [];
        this.applyPresetAnswers(this.currentQuestions);
        this.questions$ = of(this.currentQuestions);
        // A sub-category may already be selected (e.g. returning to the form):
        // keep its answers.
        this.answeredSubCategoryId = this.selectedSubCategoryId();
        this.syncFormWithSubCategory();
      });
    })

    // SCRUM-154: overload the dynamic form when the sub-category changes.
    this.formService.getQOClickEvent()
      .pipe(
        filter((event: any) => event?.index === SUB_CATEGORY_KEY && !event?.reset),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.onSubCategoryPicked());
  }

  private selectedSubCategoryId(): string | null {
    const selected = this.formService.getQuestion(SUB_CATEGORY_KEY);
    return selected?.id !== undefined && selected?.id !== null ? String(selected.id) : null;
  }

  /**
   * A different sub-category was picked: clear the whole form and start over.
   * Runs synchronously on the pick event — before SelectBigBox adds the new
   * sub-category's keyArr parameters (carType, ...) — so only the main
   * category and the new sub-category survive.
   */
  private onSubCategoryPicked(): void {
    const pickedId = this.selectedSubCategoryId();
    if (pickedId === null) return;  // intermediate "deleted" event
    const changed = this.answeredSubCategoryId !== null && pickedId !== this.answeredSubCategoryId;
    if (changed) this.clearAllAnswers();
    this.answeredSubCategoryId = pickedId;
    this.syncFormWithSubCategory(changed);
  }

  private clearAllAnswers(): void {
    const keep = new Set(['mainCategory', SUB_CATEGORY_KEY]);
    [...this.formService.QuestionMap.keys()]
      .filter(key => !keep.has(key))
      .forEach(key => this.formService.QuestionMap.delete(key));
    this.formService.setActiveQuestion(null);
  }

  ngOnInit(): void {
  }

  /**
   * SCRUM-276: a virtual main category (נדל"ן ומסחרי, סלולר, ריהוט...) presets
   * and locks some answers through mainCategory.override_fields (the server
   * sends them as the question's `value` + `disabled`). Record them as
   * answers here, for BOTH modes: the Adaline wizard skips disabled
   * questions, so their widget never ran and the preset was never sent
   * (the publish then failed on the missing sub-category).
   */
  private applyPresetAnswers(questions: QuestionBase<any>[]): void {
    // relatedField chains, for clearing dependent answers (any depth).
    this.formService.registerQuestions(questions);
    (questions || []).forEach(question => {
      const preset: any = question?.value;
      if (!question?.disabled || !preset || typeof preset !== 'object') return;
      if (this.formService.getQuestion(question.key) !== undefined) return;
      const presetId = preset?.value?.value ?? preset?.id;
      const isBigBox = String(question.controlType || '').toLowerCase().replace(/[_-]/g, '') === 'bigselectbox'
        || String(question.controlType || '').toLowerCase() === 'selectbigbox';
      if (!isBigBox) {
        this.formService.addQuestion(question.key, preset);
        return;
      }
      // A sub-category answer is the whole sub-category row (id, name,
      // keyArr...), exactly what the big-box picker stores on a click.
      this.formService.addQuestion(question.key, {...preset, id: presetId, name: preset.label});
      this.http.get<any>(this.apiUrl.getSubCategory(this.namespace))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const rows: any[] = Array.isArray(res) ? res : (res?.results || []);
            const row = rows.find(item => String(item?.id) === String(presetId));
            const current = this.formService.getQuestion(question.key);
            if (!row || String(current?.id) !== String(presetId) || current?.subCategory) return;
            this.formService.addQuestion(question.key, row);
            try {
              const params = row.keyArr ? JSON.parse(row.keyArr) : {};
              Object.keys(params).forEach(key => this.formService.addQuestion(key, params[key]));
            } catch { /* keyArr is optional */ }
          },
          error: () => {}
        });
    });
  }

  /**
   * /publish/<ns> opened directly (the floating chatbot, a refresh, a shared
   * link) skips the category gate, so `mainCategory` was never answered: the
   * manual form bounced back to /publish and the Adaline mode published
   * without it (createAdv rejects that). Resolve it from the URL namespace,
   * and load the plan list the gate normally loads.
   */
  private ensureMainCategory(): void {
    if (!(this.formService.adsRoutes || []).length) {
      this.formService.loadAdsRouts();
    }
    if (this.formService.getQuestion('mainCategory') !== undefined) return;
    const options = {headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiUrl.getJWTToken()
    })};
    this.http.get<any[]>(this.apiUrl.getMainCategory(), options)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (categories) => {
          const match = (categories || []).find(
            (c: any) => String(c?.category || '').toLowerCase() === String(this.namespace || '').toLowerCase());
          if (match && this.formService.getQuestion('mainCategory') === undefined) {
            this.formService.addQuestion('mainCategory', match);
            this.categoryLabel = match.name || this.categoryLabel;
          }
        },
        error: () => {}
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectMode(mode: PublishFormMode, questions?: QuestionBase<any>[]): void {
    // SCRUM-118: only override a question's value with whatever the user
    // already answered in this session (formService). Previously this ran
    // unconditionally, so a question the user hasn't touched yet — e.g. the
    // 'category'/'subCategory' fields on a used-item category (sale2,
    // cellular, furniture, fashion, boybaby) — had its value overwritten
    // with `undefined` (formService.getQuestion() returns undefined for a
    // key it has never seen), wiping out the pre-filled default the server
    // already sent for it (see QuestionService.dataProccessing(), which maps
    // `value: question.value` straight from the API response) right before
    // the reactive FormControl is built from `question.value`
    // (QuestionControlService.toFormGroup()). The Flutter app doesn't make
    // this mistake — DynamicFormPage._initializeFormData() only ever fills
    // in a field that has no value yet, never clobbers one the server sent.
    questions?.forEach(question => {
      const answeredValue = this.formService.getQuestion(question.key);
      if (answeredValue !== undefined) {
        question.value = answeredValue;
      }
    });
    this.selectedMode = mode;
    window.setTimeout(() => window.scrollTo({top: 0, behavior: 'smooth'}));
  }

  chooseDifferentMode(): void {
    const selectedCategory = this.formService.getQuestion('mainCategory');
    this.formService.resetForm();
    if (selectedCategory !== undefined) {
      this.formService.addQuestion('mainCategory', selectedCategory);
    }
    this.selectedMode = null;
    window.setTimeout(() => window.scrollTo({top: 0, behavior: 'smooth'}));
  }

  /**
   * SCRUM-154 — ask the server for the form of the selected sub-category.
   * Sub-categories without an overload (private / commercial / jeeps) get the
   * exact same questions back, so nothing is re-rendered for them. For an
   * overloaded sub-category (motorcycle / scooter) the questions are swapped:
   * answers that still belong to the new form (sub-category, images, contact
   * details, ...) are kept, answers of fields that no longer exist are dropped.
   */
  /** @param restart rebuild the form from fresh questions even if unchanged. */
  private syncFormWithSubCategory(restart = false): void {
    const subCategoryId = this.selectedSubCategoryId();
    if (subCategoryId === null) return;
    if (!restart && subCategoryId === this.loadedSubCategoryId) return;

    const requestId = ++this.reloadSequence;
    this.service.getServerQuestions(this.namespace, subCategoryId).subscribe((res) => {
      res.subscribe((questions: QuestionBase<any>[]) => {
        if (requestId !== this.reloadSequence || !questions) return;  // a newer pick won
        this.loadedSubCategoryId = subCategoryId;
        // Same form and no restart needed (e.g. first pick of a car type) -> keep it.
        if (!restart && this.signature(questions) === this.signature(this.currentQuestions)) return;
        this.swapQuestions(questions);
      });
    });
  }

  private swapQuestions(questions: QuestionBase<any>[]): void {
    const previous = this.currentQuestions;
    this.clearAnswersNotInForm(previous, questions);

    questions.forEach(question => {
      const answeredValue = this.formService.getQuestion(question.key);
      if (answeredValue !== undefined) question.value = answeredValue;
    });

    // Continue right after the sub-category picker: the first unanswered
    // question of the next section.
    const nextQuestion = questions.find(question =>
      question.key !== SUB_CATEGORY_KEY &&
      !question.disabled &&
      this.formService.getQuestion(question.key) === undefined &&
      (question.section as any)?.number !== (this.findQuestion(questions, SUB_CATEGORY_KEY)?.section as any)?.number
    );
    this.formService.setActiveQuestion(nextQuestion?.key ?? null);

    this.currentQuestions = questions;
    this.applyPresetAnswers(questions);
    this.questions$ = of(questions);
    this.formVersion++;
  }

  private clearAnswersNotInForm(previous: QuestionBase<any>[], next: QuestionBase<any>[]): void {
    previous.forEach(oldQuestion => {
      if (oldQuestion.key === SUB_CATEGORY_KEY) return;
      if (this.formService.getQuestion(oldQuestion.key) === undefined) return;
      const newQuestion = this.findQuestion(next, oldQuestion.key);
      const sameField = !!newQuestion &&
        newQuestion.controlType === oldQuestion.controlType &&
        newQuestion.callBackFunction === oldQuestion.callBackFunction;
      if (!sameField) this.formService.deleteQuestion(oldQuestion.key);
    });
  }

  private findQuestion(questions: QuestionBase<any>[], key: string): QuestionBase<any> | undefined {
    return questions.find(question => question.key === key);
  }

  private signature(questions: QuestionBase<any>[]): string {
    return (questions || [])
      .map(question => {
        const section = question.section as any;
        return [question.key, question.controlType, question.callBackFunction, question.required,
          section?.sectionKey, section?.number, section?.description].join('|');
      })
      .join(';');
  }

}
